import {
  Avatar,
  Button,
  Container,
  Grid,
  Link,
  makeStyles,
  TextField,
  Typography,
  CircularProgress
} from '@material-ui/core';
import React, { FC, useState } from 'react';
import { LockOutlined } from '@material-ui/icons';
import { useForm } from 'react-hook-form';
import { Auth } from 'aws-amplify';
import { useAuth } from '../../../stores/AuthStore';
import { StateOnClose } from './AuthDialog';
import { EmailField, PasswordField } from './SignUp';

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  }
}));

interface IFormLogin {
  email: string;
  password: string;
  code: string;
}

interface IProps {
  goSignUp: () => void;
  onClose?: (state: StateOnClose) => void;
}

enum TypeUI {
  ForgotPassword = 'forgot_password',
  SubmitForgotPassword = 'submit_forgot_password',
  SignIn = 'sign_in'
}

export const Login: FC<IProps> = props => {
  const classes = useStyles();
  const authStore = useAuth();
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState<string>();
  const [typeUI, setTypeUI] = useState(TypeUI.SignIn);
  const [emailChangePassword, setEmailChangePassword] = useState<string>();

  const setMessageError = (m?: string) => {
    setIsError(true);
    setMessage(m);
  };
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset
  } = useForm<IFormLogin>();
  const onSubmit = (data: IFormLogin) => {
    setLoading(true);
    if (typeUI === TypeUI.ForgotPassword) {
      return Auth.forgotPassword(data.email)
        .then(() => {
          setEmailChangePassword(data.email);
          setTypeUI(TypeUI.SubmitForgotPassword);
        })
        .finally(() => setLoading(false));
    }
    if (typeUI === TypeUI.SubmitForgotPassword) {
      return Auth.forgotPasswordSubmit(
        emailChangePassword as string,
        data.code,
        data.password
      )
        .then(() => {
          setTypeUI(TypeUI.SignIn);
          setEmailChangePassword(undefined);
          setMessageError(undefined);
          setMessage('Change password successfully');
          setIsError(false);
        })
        .catch(error => {
          setMessageError(error?.message);
        })
        .finally(() => setLoading(false));
    }
    Auth.signIn(data.email, data.password)
      .then(async () => {
        await authStore.getProfile();
        setLoading(false);
        setMessageError(undefined);
        props.onClose?.(StateOnClose.SignInSuccess);
      })
      .catch(error => {
        setMessageError(error?.message);
      })
      .finally(() => setLoading(false));
  };

  const textButton = () => {
    switch (typeUI) {
      case TypeUI.SignIn:
        return 'Sign In';
      case TypeUI.ForgotPassword:
        return 'Forgot Password';
      case TypeUI.SubmitForgotPassword:
        return 'Change Password';
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlined />
        </Avatar>
        <Typography variant="h5">
          {typeUI === TypeUI.SignIn ? 'Sign In' : 'Forgot password'}
        </Typography>

        <form className={classes.form} noValidate>
          {typeUI !== TypeUI.SubmitForgotPassword ? (
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              autoFocus
              helperText={errors?.email?.message}
              error={!!errors?.email}
              name="email"
              inputRef={register(EmailField)}
            />
          ) : null}
          {typeUI === TypeUI.SubmitForgotPassword ? (
            <TextField
              margin="normal"
              required
              fullWidth
              label="Verification Code"
              autoFocus
              helperText={errors?.code?.message}
              error={!!errors?.code}
              name="code"
              inputRef={register({
                required: 'This field is required'
              })}
            />
          ) : null}
          {typeUI !== TypeUI.ForgotPassword ? (
            <TextField
              margin="normal"
              required
              fullWidth
              type="password"
              label="Password"
              error={!!errors?.password}
              helperText={errors?.password?.message}
              name="password"
              inputRef={register(PasswordField)}
            />
          ) : null}

          {!message || (
            <Typography color={isError ? 'error' : 'primary'}>
              {message}
            </Typography>
          )}

          <Button
            type="button"
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            className={classes.submit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={16} /> : textButton()}
          </Button>
          <Grid container spacing={1}>
            <Grid item xs>
              <Link
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  reset();
                  setMessageError(undefined);
                  setTypeUI(
                    typeUI === TypeUI.SignIn
                      ? TypeUI.ForgotPassword
                      : TypeUI.SignIn
                  );
                }}
                variant="body2"
              >
                {typeUI === TypeUI.SignIn ? 'Forgot password?' : 'Sign In'}
              </Link>
            </Grid>
            <Grid item>
              <Link
                style={{ cursor: 'pointer' }}
                onClick={() => props?.goSignUp?.()}
                variant="body2"
              >
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </form>
      </div>
    </Container>
  );
};
